import React, { useState, useEffect, useRef } from "react";
import { EmpButton as Button } from '@empowernow/ui';
import { SearchOutlined, UpOutlined, DownOutlined, ReloadOutlined } from '@ant-design/icons';

interface AdvancedSearchBarProps {
  onSearch: (params: Record<string, string>) => void;
  searchParameters: Record<string, string>;
  basicSearchKey?: string;
  isLoading?: boolean;
  onRefresh?: () => void;
  viewSwitcher?: React.ReactNode;
}

export function AdvancedSearchBar({
  onSearch,
  searchParameters,
  basicSearchKey = "TextToSearch",
  isLoading = false,
  onRefresh,
  viewSwitcher,
}: AdvancedSearchBarProps) {
  const [advancedSearchFields, setAdvancedSearchFields] = useState<Record<string, string>>({});
  const [advancedSearch, setAdvancedSearch] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const userToggledRef = useRef<boolean>(false); // Track if user manually toggled

  useEffect(() => {
    setSearchQuery(searchParameters[basicSearchKey] || "");

    // Extract advanced search fields (all fields except basicSearchKey)
    const newAdvancedFields = Object.fromEntries(
      Object.entries(searchParameters).filter(([key]) => key !== basicSearchKey)
    );

    // Update field definitions if new fields are added
    if (Object.keys(newAdvancedFields).length > 0) {
      setAdvancedSearchFields(prevFields => {
        // Merge new fields with existing ones to preserve field definitions
        const mergedFields = { ...prevFields };
        Object.keys(newAdvancedFields).forEach(key => {
          if (!(key in mergedFields)) {
            mergedFields[key] = '';
          }
        });
        return mergedFields;
      });
    }

    // Update values in advanced search
    setAdvancedSearch(prevSearch => {
      const updatedSearch = { ...prevSearch };
      Object.keys(newAdvancedFields).forEach(key => {
        updatedSearch[key] = searchParameters[key] || '';
      });
      return updatedSearch;
    });

    // Automatically expand if any searchParams have a truthy value
    // But only if user hasn't manually toggled the state
    if (!userToggledRef.current) {
      const hasTruthyValue = Object.values(newAdvancedFields).some(Boolean);
      if (hasTruthyValue) {
        setIsExpanded(true);
      }
    }
  }, [searchParameters, basicSearchKey]);

  const handleSearch = () => {
    let params: Record<string, string> = {};
    
    if (isExpanded) {
      // Use expanded search parameters, filtering out empty values
      params = Object.fromEntries(
        Object.entries(advancedSearch).filter(([_, value]) => value.trim() !== "")
      );
    } else {
      // Use main search term with basicSearchKey
      if (searchQuery.trim()) {
        params = { [basicSearchKey]: searchQuery.trim() };
      }
    }
    
    onSearch(params);
  };

  const hasAdvancedSearchFields = Object.keys(advancedSearchFields).length > 0;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  return (
    <div style={{ marginBottom: '32px', paddingRight: '32px' }}>
      <div style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        marginBottom: isExpanded ? '16px' : '0'
      }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <div style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--main-text-secondary)',
            zIndex: 1,
            fontSize: '14px'
          }}>
            <SearchOutlined />
          </div>
          <input
            type="text"
            placeholder={isExpanded ? "Use advanced search fields below" : "Enter search"}
            value={isExpanded ? '' : searchQuery}
            onChange={(e) => {
              if (!isExpanded) {
                setSearchQuery(e.target.value);
              }
            }}
            onKeyDown={handleKeyDown}
            disabled={isExpanded}
            style={{
              width: '100%',
              background: 'var(--dropdown-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              padding: '8px 12px 8px 36px',
              color: 'var(--main-text)',
              fontSize: '14px',
              outline: 'none',
              opacity: isExpanded ? 0.5 : 1,
              cursor: isExpanded ? 'not-allowed' : 'text'
            }}
          />
        </div>
        
        {hasAdvancedSearchFields && (
          <Button
            onClick={() => {
              userToggledRef.current = true; // Mark that user manually toggled
              setSearchQuery("");
              setIsExpanded(!isExpanded);
            }}
            style={{
              background: 'var(--dropdown-bg)',
              border: '1px solid var(--border-color)',
              color: 'var(--main-text-secondary)',
              minWidth: '40px',
              height: '34px',
              cursor: 'pointer',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isExpanded ? <UpOutlined /> : <DownOutlined />}
          </Button>
        )}
        
        <Button
          onClick={handleSearch}
          style={{
            background: 'var(--selected-bg)',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            color: 'var(--main-text)',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Search
        </Button>
        
        {onRefresh && (
          <Button
            onClick={onRefresh}
            disabled={isLoading}
            style={{
              background: 'var(--dropdown-bg)',
              border: '1px solid var(--border-color)',
              color: 'var(--main-text-secondary)',
              minWidth: '40px',
              height: '34px',
              cursor: 'pointer',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ReloadOutlined spin={isLoading} />
          </Button>
        )}
        
        {viewSwitcher && (
          <div style={{ marginLeft: '8px' }}>
            {viewSwitcher}
          </div>
        )}
      </div>
      
      {/* Expanded Search Fields */}
      {isExpanded && Object.keys(advancedSearchFields).length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px',
          background: 'var(--preview-bg)',
          borderRadius: '8px',
          border: '1px solid var(--border-color)'
        }}>
          {Object.keys(advancedSearchFields).map((key) => (
            <div key={key}>
              <label style={{
                display: 'block',
                color: 'var(--main-text)',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '4px'
              }}>
                {key}
              </label>
              <input
                type="text"
                placeholder={`By ${key}`}
                value={advancedSearch[key] || ''}
                onChange={(e) => {
                  setAdvancedSearch({
                    ...advancedSearch,
                    [key]: e.target.value
                  });
                }}
                onKeyDown={handleKeyDown}
                style={{
                  width: '100%',
                  background: 'var(--dropdown-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  color: 'var(--main-text)',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
